import { TourProvider, useTour } from "@reactour/tour"
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import { useNavigate } from "react-router"

import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { AI_CHAT_MOCK_QUESTION_KEY } from "../config/ai-chat-mock-data"
import { createAllTours } from "../config/tours.config"
import {
  ONBOARDING_DEMO_EXTRACTION_FLAG,
  ONBOARDING_DEMO_QUESTION_ANSWERING_FLAG,
  ONBOARDING_EXTRACTION_STEP_ID,
  ONBOARDING_QUESTION_ANSWERING_STEP_ID,
  ONBOARDING_UPLOAD_STEP_ID,
} from "../demoData"
import type { Tour, TourId, TourState } from "../types/tour.types"

import {
  TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { formatOnboardingStep, updateOnboardingStep } from "@/utils/onboarding"

import { TourCelebrationModal } from "../components/TourCelebrationModal"
import { TourNextButton, TourPrevButton } from "../components/TourNavButtons"
import { TourTooltip } from "../components/TourTooltip"

import { isStepWithoutGlow } from "../constants"
import { createTourStyles } from "../styles/tourProviderStyles"
import { normalizePosition } from "../utils/positionUtils"
import { tourStorage } from "../utils/storage"
import { getCurrentPath, waitForElementById } from "../utils/waitForElement"

interface OnboardingContextType extends TourState {
  startTour: (tourId: TourId) => void
  skipTour: () => void
  pauseTour: () => void
  skipOnboarding: () => void
  completeTour: () => void
  goToPreviousStep: () => void
  exitTour: () => void
  getCurrentTour: () => Tour | null
  isTourCompleted: (tourId: TourId) => boolean
  handleHighlightedAction: (
    targetElementId: string,
    options?: { performNavigation?: boolean },
  ) => void
  showWelcomeModal: boolean
  triggerWelcomeModal: () => void
  closeWelcomeModal: () => void
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
)

const DEFAULT_CONTEXT: OnboardingContextType = {
  activeTourId: null,
  currentStepIndex: 0,
  isActive: false,
  stepRoutes: {},
  startTour: () => {},
  skipTour: () => {},
  pauseTour: () => {},
  skipOnboarding: () => {},
  completeTour: () => {},
  goToPreviousStep: () => {},
  exitTour: () => {},
  getCurrentTour: () => null,
  isTourCompleted: () => false,
  handleHighlightedAction: () => {},
  showWelcomeModal: false,
  triggerWelcomeModal: () => {},
  closeWelcomeModal: () => {},
}

const EMPTY_STATE: TourState = {
  activeTourId: null,
  currentStepIndex: 0,
  isActive: false,
  stepRoutes: {},
}

const getNextTourInSequence = (tours: Tour[], tourId: TourId): Tour | null => {
  const currentIndex = tours.findIndex((tour) => tour.id === tourId)
  if (currentIndex === -1) return null
  return tours[currentIndex + 1] ?? null
}

const TourStateSync: React.FC<{ stepIndex: number }> = ({ stepIndex }) => {
  const { setCurrentStep } = useTour()
  React.useEffect(() => {
    setCurrentStep(stepIndex)
  }, [setCurrentStep, stepIndex])
  return null
}

// ============================================================================
// Provider
// ============================================================================

const OnboardingTourProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { translate } = useTranslation()
  const navigate = useNavigate()

  const chatHistoryEnabled = useFeatureFlag("CHAT_HISTORY")
  const tours = useMemo(
    () => createAllTours({ CHAT_HISTORY: chatHistoryEnabled }),
    [chatHistoryEnabled],
  )

  // State
  const [state, setState] = useState<TourState>(() => {
    const saved = tourStorage.getState()
    if (saved?.isActive) {
      return {
        ...saved,
        stepRoutes:
          Object.keys(saved.stepRoutes || {}).length > 0
            ? saved.stepRoutes
            : { [saved.currentStepIndex]: getCurrentPath() },
      }
    }
    return EMPTY_STATE
  })

  const [celebrationModal, setCelebrationModal] = useState<{
    isOpen: boolean
    completedTourId: TourId | null
  }>({
    isOpen: false,
    completedTourId: null,
  })

  const [showWelcomeModal, setShowWelcomeModal] = useState(false)

  // Persist state
  React.useEffect(() => {
    if (state.isActive) {
      tourStorage.setState(state)
    } else {
      tourStorage.clearState()
    }
  }, [state])

  React.useEffect(() => {
    if (state.activeTourId) {
      tourStorage.saveProgress(state.activeTourId, {
        stepIndex: state.currentStepIndex,
        stepRoutes: state.stepRoutes,
      })
    }
  }, [state.activeTourId, state.currentStepIndex, state.stepRoutes])

  // Core helpers
  const getCurrentTour = useCallback((): Tour | null => {
    return state.activeTourId
      ? tours.find((t) => t.id === state.activeTourId) || null
      : null
  }, [state.activeTourId, tours])

  const isTourCompleted = useCallback(
    (tourId: TourId): boolean => {
      const tour = tours.find((t) => t.id === tourId)
      return tour ? tourStorage.isTourCompleted(tour.storageKey) : false
    },
    [tours],
  )

  const resolveTourId = useCallback(
    (requestedId: TourId): TourId => {
      const progressMap = tourStorage.getAllProgress()
      if (progressMap[requestedId]) return requestedId

      const resumeEntry = tours.find((tour) => progressMap[tour.id])
      if (resumeEntry) return resumeEntry.id as TourId

      return requestedId
    },
    [tours],
  )

  // Tour actions
  const startTour = useCallback(
    (tourId: TourId) => {
      const effectiveTourId = resolveTourId(tourId)
      const tour = tours.find((t) => t.id === effectiveTourId)
      if (!tour) return

      const savedProgress = tourStorage.getProgress(effectiveTourId)
      const resumeStep = savedProgress
        ? Math.min(savedProgress.stepIndex ?? 0, tour.steps.length - 1)
        : 0
      const resumeRoutes = savedProgress?.stepRoutes
      const initialRoute =
        resumeRoutes?.[resumeStep] ??
        tour.steps[resumeStep]?.route ??
        getCurrentPath()

      updateOnboardingStep(
        formatOnboardingStep(effectiveTourId, Math.max(resumeStep, 0)),
      ).catch((e) => console.warn("[Onboarding] Sync failed:", e))

      setState({
        activeTourId: effectiveTourId,
        currentStepIndex: resumeStep,
        isActive: true,
        stepRoutes:
          resumeRoutes && Object.keys(resumeRoutes).length > 0
            ? resumeRoutes
            : { [resumeStep]: initialRoute },
      })

      if (initialRoute && initialRoute !== getCurrentPath()) {
        navigate(initialRoute)
      }
    },
    [navigate, resolveTourId, tours],
  )

  const skipTour = useCallback(() => {
    const tour = getCurrentTour()
    if (tour) {
      tourStorage.markTourCompleted(tour.storageKey)
      tourStorage.clearProgress(tour.id)
      updateOnboardingStep(
        formatOnboardingStep(tour.id, state.currentStepIndex),
      ).catch((e) => console.warn("[Onboarding] Sync failed:", e))
    }
    tourStorage.clearState()
    setState(EMPTY_STATE)
  }, [getCurrentTour, state.currentStepIndex])

  const pauseTour = useCallback(() => {
    const tour = getCurrentTour()
    if (!tour) {
      tourStorage.clearState()
      setState(EMPTY_STATE)
      return
    }

    tourStorage.saveProgress(tour.id, {
      stepIndex: state.currentStepIndex,
      stepRoutes: state.stepRoutes,
    })

    updateOnboardingStep(
      formatOnboardingStep(tour.id, state.currentStepIndex),
    ).catch((e) => console.warn("[Onboarding] Sync failed:", e))

    tourStorage.clearState()
    setState(EMPTY_STATE)
  }, [getCurrentTour, state.currentStepIndex, state.stepRoutes])

  const skipTourRef = React.useRef(skipTour)
  const navigateRef = React.useRef(navigate)
  const isActiveRef = React.useRef(state.isActive)

  React.useEffect(() => {
    skipTourRef.current = skipTour
    navigateRef.current = navigate
    isActiveRef.current = state.isActive
  }, [skipTour, navigate, state.isActive])

  React.useEffect(() => {
    const handlePopState = () => {
      // Only handle if tour is actually active
      if (!isActiveRef.current) return
      // Exit tour and go to home (same as X button)
      skipTourRef.current()
      navigateRef.current("/", { replace: true })
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [])

  const skipOnboarding = useCallback(() => {
    updateOnboardingStep("skipped").catch((e) =>
      console.warn("[Onboarding] Sync failed:", e),
    )
  }, [])

  const completeTour = useCallback(() => {
    const tour = getCurrentTour()
    if (tour) {
      tourStorage.markTourCompleted(tour.storageKey)
      tourStorage.clearProgress(tour.id)

      const nextTour = getNextTourInSequence(tours, tour.id)
      if (nextTour) {
        const firstRoute =
          nextTour.steps[0]?.route ??
          nextTour.steps[0]?.nextRoute ??
          getCurrentPath()

        tourStorage.saveProgress(nextTour.id, {
          stepIndex: 0,
          stepRoutes: firstRoute ? { 0: firstRoute } : {},
        })
      }
    }
    tourStorage.clearState()
    setState(EMPTY_STATE)
    if (tour?.id)
      setCelebrationModal({ isOpen: true, completedTourId: tour.id })
  }, [getCurrentTour, tours])

  const exitTour = useCallback(() => {
    pauseTour()
    setShowWelcomeModal(false)
    navigate("/", { replace: true })
  }, [pauseTour, navigate, setShowWelcomeModal])

  // Navigation
  const goToPreviousStep = useCallback(() => {
    if (!state.isActive || state.currentStepIndex === 0) return
    const tour = getCurrentTour()
    if (!tour) return

    let targetIdx = state.currentStepIndex - 1
    while (
      targetIdx >= 0 &&
      (tour.steps[targetIdx]?.skipOnBackNavigation ||
        tour.steps[targetIdx]?.hideFromStepCount)
    )
      targetIdx--
    if (targetIdx < 0) return

    const targetStep = tour.steps[targetIdx]
    if (!targetStep) return

    if (targetStep.id === ONBOARDING_EXTRACTION_STEP_ID)
      tourStorage.setDemoFlag(ONBOARDING_DEMO_EXTRACTION_FLAG)
    if (targetStep.id === ONBOARDING_QUESTION_ANSWERING_STEP_ID)
      tourStorage.setDemoFlag(ONBOARDING_DEMO_QUESTION_ANSWERING_FLAG)

    const targetRoute =
      targetStep.route ?? state.stepRoutes?.[targetIdx] ?? getCurrentPath()

    setState((prev) => {
      const routes = { ...prev.stepRoutes }
      Object.keys(routes).forEach((k) => {
        if (Number(k) > targetIdx) delete routes[Number(k)]
      })
      if (targetRoute) routes[targetIdx] = targetRoute
      const updated = {
        ...prev,
        currentStepIndex: targetIdx,
        stepRoutes: routes,
      }
      tourStorage.setState(updated)
      return updated
    })

    navigate(targetRoute, {
      state: { tourBackTimestamp: Date.now(), tourBackToStepId: targetStep.id },
      replace: targetRoute === getCurrentPath(),
    })
  }, [state, getCurrentTour, navigate])

  const handleHighlightedAction = useCallback(
    (targetElementId: string, options?: { performNavigation?: boolean }) => {
      if (!state.isActive) return
      const tour = getCurrentTour()
      if (!tour) return

      const currentStep = tour.steps[state.currentStepIndex]
      if (!currentStep || currentStep.targetElementId !== targetElementId)
        return

      const performNav = options?.performNavigation ?? false
      const nextIdx = state.currentStepIndex + 1
      const nextStep = tour.steps[nextIdx]
      const nextRoute =
        nextStep?.route ??
        (performNav && currentStep.nextRoute
          ? currentStep.nextRoute
          : getCurrentPath())

      if (state.currentStepIndex === tour.steps.length - 1) {
        completeTour()
        if (performNav && currentStep.nextRoute) {
          if (currentStep.id === ONBOARDING_UPLOAD_STEP_ID)
            tourStorage.setDemoFlag(ONBOARDING_DEMO_EXTRACTION_FLAG)
          navigate(currentStep.nextRoute)
        }
        return
      }

      const advance = async () => {
        if (performNav && currentStep.nextRoute) {
          if (currentStep.id === ONBOARDING_UPLOAD_STEP_ID)
            tourStorage.setDemoFlag(ONBOARDING_DEMO_EXTRACTION_FLAG)
          navigate(currentStep.nextRoute)
        }
        if (performNav && currentStep.nextRoute && nextStep?.targetElementId)
          await waitForElementById(nextStep.targetElementId)
        setState((prev) => {
          const updated = {
            ...prev,
            currentStepIndex: nextIdx,
            stepRoutes: { ...prev.stepRoutes, [nextIdx]: nextRoute },
          }
          tourStorage.setState(updated)
          return updated
        })
      }
      advance()
    },
    [state, getCurrentTour, completeTour, navigate],
  )

  // Celebration handlers
  const handleCelebrationContinue = useCallback(() => {
    const id = celebrationModal.completedTourId
    if (!id) return
    setCelebrationModal({ isOpen: false, completedTourId: null })
    setShowWelcomeModal(false)
    if (id === "questionnaire") setTimeout(() => startTour("ai-chat"), 100)
    else if (id === "ai-chat") setTimeout(() => startTour("database"), 100)
    else {
      updateOnboardingStep("completed").catch((e) =>
        console.warn("[Onboarding] Sync failed:", e),
      )
      navigate("/", { replace: true })
    }
  }, [
    celebrationModal.completedTourId,
    startTour,
    navigate,
    setShowWelcomeModal,
  ])

  const handleCelebrationExit = useCallback(() => {
    if (celebrationModal.completedTourId === "database")
      updateOnboardingStep("completed").catch((e) =>
        console.warn("[Onboarding] Sync failed:", e),
      )
    setCelebrationModal({ isOpen: false, completedTourId: null })
    setShowWelcomeModal(false)
    navigate("/", { replace: true })
  }, [celebrationModal.completedTourId, navigate, setShowWelcomeModal])

  // Auto-advance
  React.useEffect(() => {
    if (!state.isActive) return
    const tour = getCurrentTour()
    const step = tour?.steps[state.currentStepIndex]
    if (!step?.autoAdvanceDelay) return
    const timer = setTimeout(
      () =>
        handleHighlightedAction(step.targetElementId, {
          performNavigation: Boolean(step.nextRoute),
        }),
      step.autoAdvanceDelay,
    )
    return () => clearTimeout(timer)
  }, [
    state.isActive,
    state.currentStepIndex,
    getCurrentTour,
    handleHighlightedAction,
  ])

  // Memoized steps
  const currentTour = getCurrentTour()
  const reactourSteps = useMemo(() => {
    if (!currentTour) return []
    let visibleNum = 0
    const numMap = currentTour.steps.map((s) =>
      s.hideFromStepCount ? null : ++visibleNum,
    )
    return currentTour.steps.map((step, i) => ({
      selector: `#${step.targetElementId}`,
      position: normalizePosition(step.position),
      stepInteraction: true,
      padding: { mask: step.padding ?? 0, popover: 0 },
      content: (
        <TourTooltip
          title={translate(step.title as TranslationKeys)}
          description={translate(step.description as TranslationKeys)}
          mockQuestion={
            step.showMockQuestion
              ? translate(AI_CHAT_MOCK_QUESTION_KEY as TranslationKeys)
              : undefined
          }
          showSendIcon={step.showSendIcon}
          autoAdvanceDelay={step.autoAdvanceDelay}
          onExit={exitTour}
          exitLabel={translate("TOUR/EXIT")}
          currentStep={numMap[i] ?? undefined}
        />
      ),
    }))
  }, [currentTour, translate, exitTour])

  const stepConfig = currentTour?.steps[state.currentStepIndex]
  const showGlow = stepConfig?.id && !isStepWithoutGlow(stepConfig.id)
  const styles = createTourStyles({
    position: stepConfig?.position,
    stepPadding: stepConfig?.padding ?? 0,
  })

  return (
    <OnboardingContext.Provider
      value={{
        ...state,
        startTour,
        skipTour,
        pauseTour,
        skipOnboarding,
        completeTour,
        goToPreviousStep,
        exitTour,
        getCurrentTour,
        isTourCompleted,
        handleHighlightedAction,
        showWelcomeModal,
        triggerWelcomeModal: () => setShowWelcomeModal(true),
        closeWelcomeModal: () => setShowWelcomeModal(false),
      }}
    >
      <TourCelebrationModal
        isOpen={celebrationModal.isOpen}
        completedTourId={celebrationModal.completedTourId}
        onContinue={handleCelebrationContinue}
        onExit={handleCelebrationExit}
      />
      {reactourSteps.length > 0 ? (
        <div className={showGlow ? "tour-glow-active" : undefined}>
          <TourProvider
            key={state.isActive ? "active" : "inactive"}
            steps={reactourSteps}
            defaultOpen={state.isActive}
            startAt={state.currentStepIndex}
            showBadge={false}
            disableDotsNavigation
            showNavigation
            disableKeyboardNavigation
            onClickHighlighted={() => {
              const s = currentTour?.steps[state.currentStepIndex]
              if (s)
                handleHighlightedAction(s.targetElementId, {
                  performNavigation: Boolean(s.nextRoute),
                })
            }}
            onClickMask={() => {}}
            onClickClose={exitTour}
            keyboardHandler={(event) => {
              if (event.key === "Escape") {
                exitTour()
              }
            }}
            afterOpen={(t) =>
              t?.scrollIntoView({ behavior: "smooth", block: "center" })
            }
            styles={styles}
            prevButton={({ currentStep }) => (
              <TourPrevButton
                currentStep={currentStep}
                onBack={goToPreviousStep}
                backLabel={translate("TOUR/BACK")}
              />
            )}
            nextButton={({ currentStep, steps, setIsOpen }) => (
              <TourNextButton
                isLastStep={currentStep === (steps?.length || 0) - 1}
                isFinalTour={currentTour?.id === "database"}
                needsManualAdvance={
                  currentTour?.steps[currentStep]?.showNextButton ?? false
                }
                onComplete={() => {
                  setIsOpen(false)
                  setTimeout(completeTour, 50)
                }}
                onAdvance={() => {
                  const s = currentTour?.steps[currentStep]
                  if (s)
                    handleHighlightedAction(s.targetElementId, {
                      performNavigation: true,
                    })
                }}
                finishLabel={translate("TOUR/FINISH")}
                allSetLabel={translate("TOUR/ALL_SET")}
                nextLabel={translate("TOUR/NEXT")}
              />
            )}
          >
            <TourStateSync stepIndex={state.currentStepIndex} />
            {children}
          </TourProvider>
        </div>
      ) : (
        children
      )}
    </OnboardingContext.Provider>
  )
}

// Gate: with ONBOARDING_TOUR off no context is mounted at all, so every
// consumer falls through to DEFAULT_CONTEXT below — `isActive` stays false and
// `startTour` / `triggerWelcomeModal` are no-ops. Fails closed, because the
// flag defaults to false in DEFAULT_FLAGS.
export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const onboardingTourEnabled = useFeatureFlag("ONBOARDING_TOUR")

  if (!onboardingTourEnabled) {
    return <>{children}</>
  }

  return <OnboardingTourProvider>{children}</OnboardingTourProvider>
}

export const useOnboarding = () =>
  useContext(OnboardingContext) ?? DEFAULT_CONTEXT
